import { Message } from '@/types/messages';
import { useState } from 'react';

interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  onDelete?: (messageId: string) => void;
}

const getSenderLabel = (senderRole: string) => {
  switch (senderRole) {
    case 'admin':
      return 'Support Team';
    case 'editor':
      return 'Editor';
    case 'client':
      return 'Client';
    default:
      return 'User';
  }
};

const getSenderColor = (senderRole: string) => {
  switch (senderRole) {
    case 'admin':
      return '#10b981'; // Green for admin/support
    case 'editor':
      return '#3b82f6'; // Blue for editor
    case 'client':
      return '#8b5cf6'; // Purple for client
    default:
      return '#A1A1AA';
  }
};

export default function MessageBubble({ message, isOwnMessage, onDelete }: MessageBubbleProps) {
  const [isHovered, setIsHovered] = useState(false);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div 
      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4 group`}
      style={{
        animation: 'messageSlideIn 0.3s ease-out',
      }}
    >
      {/* Avatar for other users' messages */}
      {!isOwnMessage && (
        <div 
          className="w-8 h-8 rounded-full mr-2 flex-shrink-0 flex items-center justify-center self-end mb-1"
          style={{
            backgroundColor: `${getSenderColor(message.sender_role)}22`,
            border: `1px solid ${getSenderColor(message.sender_role)}40`,
          }}
        >
          {message.sender_role === 'admin' && (
            <svg className="w-4 h-4" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )}
          {message.sender_role === 'editor' && (
            <svg className="w-4 h-4" style={{ color: '#3b82f6' }} fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          )}
          {message.sender_role === 'client' && (
            <svg className="w-4 h-4" style={{ color: '#8b5cf6' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      )}

      <div
        className={`max-w-[85%] sm:max-w-[70%] px-4 py-2.5 relative transition-all duration-200 ${isOwnMessage ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md'}`}
        style={{
          background: isOwnMessage ? 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' : 'rgba(255, 255, 255, 0.055)',
          color: isOwnMessage ? '#FFFFFF' : '#E5E7EB',
          border: isOwnMessage ? '1px solid rgba(139, 92, 246, 0.4)' : '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: isOwnMessage
            ? (isHovered ? '0 6px 20px rgba(124, 58, 237, 0.4)' : '0 4px 14px rgba(124, 58, 237, 0.25)')
            : (isHovered ? '0 4px 14px rgba(0, 0, 0, 0.35)' : '0 2px 8px rgba(0, 0, 0, 0.22)'),
          transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Delete button (visible on hover if onDelete is provided) */}
        {onDelete && isHovered && (
          <button
            onClick={() => onDelete(message.id)}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200"
            style={{
              backgroundColor: '#EF4444',
              border: '2px solid #0B0D17',
              color: '#FFFFFF',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#DC2626';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#EF4444';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Delete message"
            aria-label="Delete message"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
        {/* Sender badge - show for all messages to clarify who sent it */}
        <div 
          className="text-xs font-semibold mb-1 flex items-center gap-1"
          style={{ color: isOwnMessage ? 'rgba(255,255,255,0.75)' : getSenderColor(message.sender_role) }}
        >
          {message.sender_role === 'admin' && (
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )}
          {message.sender_role === 'editor' && (
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          )}
          {message.sender_role === 'client' && (
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          )}
          {isOwnMessage ? 'You' : getSenderLabel(message.sender_role)}
        </div>

        <p className="text-sm break-words leading-relaxed">{message.message_text}</p>
        
        <div className="flex items-center justify-between mt-2 gap-2">
          <p 
            className="text-xs"
            style={{ 
              color: isOwnMessage ? 'rgba(255,255,255,0.7)' : '#9CA3AF',
              opacity: 0.9
            }}
          >
            {formatTimestamp(message.created_at)}
          </p>
          
          {/* Read receipt for own messages */}
          {isOwnMessage && (
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <svg className="w-3 h-3 -ml-2" style={{ color: '#10b981' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
